import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircle } from "lucide-react";
import Logo from "./Logo";
import { SUPPORT_EMAIL, WHATSAPP_LINK } from "../config";

const CONTENT = {
    privacy: {
        title: "Privacy Policy",
        updated: "Last updated: October 2026",
        intro: "At Drvn Dream (“we”, “our”), your privacy matters. This policy explains what information we collect when you purchase the 12,000+ eBook Mega Bundle, how we use it, and the choices you have.",
        sections: [
            [
                "1. Information We Collect",
                [
                    "Order details you provide at checkout or on support: your name, email address and WhatsApp number.",
                    "Payment confirmation data (order ID, amount and status) received from our payment gateway. We never see or store your card details, UPI PIN or netbanking credentials.",
                    "Basic usage data collected through analytics tools, such as pages visited, approximate city-level location, device type and browser — to understand how the site is used.",
                ],
            ],
            [
                "2. How We Use Your Information",
                [
                    "To deliver the eBook bundle and download links to you.",
                    "To provide support over WhatsApp and email, including resending your access link.",
                    "To process orders, prevent fraud and keep records as required by law.",
                    "To improve the website and the bundle — we use aggregate analytics and never spam you.",
                ],
            ],
            [
                "3. Payments",
                [
                    "All payments are processed securely by a third-party payment gateway. Your payment information is collected and stored by the gateway under its own privacy policy. We only receive a confirmation that your payment succeeded.",
                ],
            ],
            [
                "4. Cookies & Analytics",
                [
                    "This site uses cookies and similar technologies to function properly and to measure traffic anonymously. You can disable cookies in your browser — the site will continue to work.",
                ],
            ],
            [
                "5. Sharing Your Data",
                [
                    "We share your information only with the payment gateway (to process payments) and with email/WhatsApp delivery services (to send your download link). We never sell, rent or trade your personal data to anyone.",
                ],
            ],
            [
                "6. Data Security & Retention",
                [
                    "We use reasonable technical and organisational measures to protect your information. We keep order records only as long as needed for delivery, support, accounting and legal purposes.",
                ],
            ],
            [
                "7. Your Rights",
                [
                    `You can ask us for a copy of your data, request corrections, or ask us to delete your records anytime by writing to ${SUPPORT_EMAIL} or messaging us on WhatsApp.`,
                ],
            ],
            [
                "8. Contact",
                [
                    `Questions about this policy? Email ${SUPPORT_EMAIL} or message us on WhatsApp — we usually reply within a few hours.`,
                ],
            ],
        ],
    },
    terms: {
        title: "Terms & Conditions",
        updated: "Last updated: October 2026",
        intro: "Welcome to Drvn Dream. By purchasing or using the 12,000+ eBook Mega Bundle (“the Bundle”), you agree to these terms. Please read them once — they keep things fair for everyone.",
        sections: [
            [
                "1. The Product",
                [
                    "The Bundle is a digital product: 12,000+ eBooks in PDF/EPUB formats (with bonus content where mentioned), delivered as a download link on your email and WhatsApp after successful payment.",
                    "No physical product is shipped. You need an internet connection to download the files.",
                ],
            ],
            [
                "2. Licence & Acceptable Use",
                [
                    "You get a personal, non-exclusive, non-transferable licence to read and use the Bundle for your own learning.",
                    "You may not resell, redistribute, upload the eBooks to public websites, or share your download link. Sharing breaks these terms and ends your licence without refund.",
                ],
            ],
            [
                "3. Pricing & Payment",
                [
                    "All prices are in Indian Rupees (₹). Payment is collected securely through our payment gateway.",
                    "Offer prices and discounts may change without notice. The price you see at checkout is the price you pay — no hidden charges.",
                ],
            ],
            [
                "4. Delivery",
                [
                    "Your download link is usually delivered within minutes of payment on your email and WhatsApp.",
                    "If you don’t receive the link within 30 minutes, message us on WhatsApp or email us with your payment screenshot and we’ll resend it right away.",
                ],
            ],
            [
                "5. Refund Policy",
                [
                    "Every purchase is covered by our 24-hour money-back guarantee — if you're not satisfied for any reason, tell us within 24 hours of payment and we'll refund 100% of your money, no questions asked.",
                    "To request a refund, message us on WhatsApp or email us with your payment screenshot. Approved refunds are processed to your original payment method within 5–7 business days.",
                    "Requests made after 24 hours are not eligible, since full access is delivered instantly. Full details are on our Refund Policy page.",
                ],
            ],
            [
                "6. Intellectual Property",
                [
                    "All eBooks belong to their respective authors, publishers and rights holders. Your purchase gives you access for personal reading under the licence described above. The Drvn Dream name, logo and website design belong to us.",
                ],
            ],
            [
                "7. Educational Purpose",
                [
                    "The Bundle is for learning and personal growth. It is not financial, legal, medical or professional advice, and results depend on your own effort and application.",
                ],
            ],
            [
                "8. Limitation of Liability",
                [
                    "To the maximum extent permitted by law, our total liability for any claim related to the Bundle is limited to the amount you paid for it.",
                ],
            ],
            [
                "9. Changes to These Terms",
                [
                    "We may update these terms from time to time. The latest version will always be available on this page, and continuing to use the Bundle means you accept the updated terms.",
                ],
            ],
            [
                "10. Governing Law",
                [
                    "These terms are governed by the laws of India, and any disputes will be subject to the exclusive jurisdiction of Indian courts.",
                ],
            ],
            [
                "11. Contact",
                [
                    `For any questions about these terms, write to ${SUPPORT_EMAIL} or message us on WhatsApp.`,
                ],
            ],
        ],
    },
    refund: {
        title: "Refund Policy",
        updated: "Last updated: October 2026",
        intro: "We want you to love the 12,000+ eBook Mega Bundle. If it isn't right for you, this simple policy has you covered.",
        sections: [
            [
                "1. 24-Hour Money-Back Guarantee",
                [
                    "Every purchase comes with a full money-back guarantee, valid for 24 hours from the time of payment.",
                    "If you're not satisfied with the bundle — for any reason — just tell us within 24 hours and we'll refund 100% of your payment. No questions asked.",
                ],
            ],
            [
                "2. How to Request a Refund",
                [
                    "Message us on WhatsApp at +91 95559 25080 or email support@drvndream.in with your payment screenshot.",
                    "You can also use the green WhatsApp button on any page of this site — it opens a chat with us directly.",
                    "We confirm your refund request within a few hours, usually within minutes.",
                ],
            ],
            [
                "3. Refund Processing Time",
                [
                    "Approved refunds are processed to your original payment method within 5–7 business days, depending on your bank or UPI provider.",
                ],
            ],
            [
                "4. What's Not Covered",
                [
                    "Refund requests made after 24 hours of purchase — full access is delivered instantly, so the guarantee window is 24 hours.",
                    "Accounts found misusing the licence — reselling, redistributing or sharing the download links (see our Terms & Conditions).",
                ],
            ],
            [
                "5. Didn't Receive Your Access?",
                [
                    "If your download link never arrived, that's not a refund case — it's a fix-it-first case. Message us anytime and we'll resend your access immediately, day or night.",
                ],
            ],
            [
                "6. Contact for Refunds",
                [
                    "WhatsApp: +91 95559 25080 (fastest — usually replies within an hour)",
                    "Email: support@drvndream.in",
                    "We're available 7 days a week, morning to late night.",
                ],
            ],
        ],
    },
};

export default function LegalPage({ type }) {
    const data = CONTENT[type.replace("#/", "")] || CONTENT.privacy;

    useEffect(() => {
        document.title = `${data.title} — Drvn Dream`;
        return () => {
            document.title = "12,000+ eBook Mega Bundle — Drvn Dream ₹199";
        };
    }, [data.title]);

    return (
        <div data-testid="legal-page" className="min-h-screen bg-[#ecfdf5] pb-20">
            <div className="border-b border-emerald-100 bg-white/85 backdrop-blur-md">
                <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-4 sm:px-6">
                    <a
                        href="#"
                        data-testid="legal-back-home"
                        aria-label="Back to home"
                    >
                        <Logo size={30} />
                    </a>
                    <a
                        href={WHATSAPP_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="legal-whatsapp-link"
                        aria-label="Chat on WhatsApp"
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366] text-white shadow-md transition-transform hover:scale-105"
                    >
                        <MessageCircle size={18} />
                    </a>
                </div>
            </div>

            <motion.main
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14"
            >
                <div className="rounded-[2rem] border border-emerald-100 bg-white p-6 shadow-soft sm:p-10">
                    <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
                        {data.title}
                    </h1>
                    <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                        {data.updated}
                    </p>
                    <p className="mt-5 text-sm leading-relaxed text-slate-600 sm:text-[15px]">
                        {data.intro}
                    </p>
                    <div className="mt-9 space-y-8">
                        {data.sections.map(([heading, paras]) => (
                            <section key={heading}>
                                <h2 className="font-display text-base font-bold text-slate-900 sm:text-lg">
                                    {heading}
                                </h2>
                                {paras.map((p, i) => (
                                    <p
                                        key={i}
                                        className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-[15px]"
                                    >
                                        {p}
                                    </p>
                                ))}
                            </section>
                        ))}
                    </div>
                    <a
                        href="#"
                        data-testid="legal-back-home-bottom"
                        className="mt-12 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-5 py-2.5 text-sm font-semibold text-emerald-700 transition-all hover:-translate-y-0.5 hover:bg-emerald-50"
                    >
                        <ArrowLeft size={15} />
                        Back to the bundle
                    </a>
                </div>
            </motion.main>
        </div>
    );
}
